import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import moment from "moment";

// ASSET PATHS  –  same header/footer images used in generalIRPdf.js
const UnitechHeader = "../img/feddback_header.png";
const UnitechFooter = "../img/IR_Footer.png";

// FONT FAMILY
export const customFontFamily = {
  bold:       "bold",
  normal:     "normal",
  boldFont:   "AptosBold",
  normalFont: "AptosNormal",
};

// CONFIG  –  same shape/keys as generalIRPdf.js so header/footer/margins match exactly
export const config = {
  // Page margins
  margin:       15,
  borderMargin: 15,

  // Header / footer image heights (set 0 to hide) – same as generalIR
  headerimgHeight: 70,
  footerimgHeight: 0,

  // Page-number strip height (below footer)
  pageNoHeight: 15,

  // Gap between header image bottom and content rect top
  borderGap: 2,

  // Default font sizes
  fontSize:      10,
  fontSizeSm:    10,
  fontSizeLg:    12,
  fontSizeTitle: 15,

  // Row / cell padding inside autoTable
  cellPadding: 4,

  // Spacing added after every section (pt)
  sectionGap: 0,

  // Spacing for bottom border and bottom content between
  borderFooterGap: 15,

  // Computed helpers (derived – do not set manually) – identical formula to generalIRPdf.js
  get contentTop() {
    return this.headerimgHeight + this.borderGap + this.borderMargin + 15;
  },
  get contentBottom() {
    return this.footerimgHeight + this.pageNoHeight + this.borderGap + this.borderMargin + 5;
  },
};

const getBaseStyles = () => ({
  fontSize:    config.fontSize,
  cellPadding: config.cellPadding,
  lineColor:   [0, 0, 0],
  lineWidth:   0.5,
  textColor:   [0, 0, 0],
  fillColor:   false,
  font:        customFontFamily.normalFont,
  fontStyle:   customFontFamily.normal,
  halign:      "left",
  valign:      "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font:      customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: false,
});

const getTableMargins = () => ({
  left:   config.margin,
  right:  config.margin,
  top:    config.contentTop,
  bottom: config.contentBottom,
});

// LAYOUT STATE
const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc          = doc;
    this.pageWidth    = doc.internal.pageSize.getWidth();
    this.pageHeight   = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y            = config.contentTop;
  },
  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },
  advance(delta) {
    this.y += delta;
  },
  ensureSpace(needed = 30) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

// FONT LOADER
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes     = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  const fontResponse = await fetch(fontUrl);
  const fontBuffer   = await fontResponse.arrayBuffer();
  const fontBase64   = arrayBufferToBase64(fontBuffer);
  pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
  pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
};

// PAGE DECORATORS  –  same structure as addHeaderFooter in generalIRPdf.js
const addHeaderFooter = (doc, docNumber) => {
  const marginX = 0;
  const width   = layout.pageWidth;

  if (config.headerimgHeight > 0) {
    doc.addImage(UnitechHeader, "PNG", marginX, 0, width, config.headerimgHeight);
  }
  if (config.footerimgHeight > 0) {
    doc.addImage(
      UnitechFooter,
      "PNG",
      marginX,
      layout.pageHeight - config.footerimgHeight - 3,
      width,
      config.footerimgHeight
    );
  }
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
};

const drawMarginRect = (doc) => {
  const topSpace    = config.headerimgHeight + config.borderGap;
  const bottomSpace = config.footerimgHeight + config.pageNoHeight + config.borderGap;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(
    config.borderMargin,
    config.borderMargin + topSpace,
    layout.pageWidth  - config.borderMargin * 2,
    layout.pageHeight - config.borderMargin * 2 - topSpace - bottomSpace
  );
};

// Footer / page numbers  –  same structure as addPageNumbers in generalIRPdf.js
const addPageNumbers = (doc, totalPages, docNumber) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addHeaderFooter(doc, docNumber);
    // drawMarginRect(doc);

    const pageNoY = layout.pageHeight - config.footerimgHeight - config.borderFooterGap;

    // Page number line – left = doc-code, right = Page No : XX of XX
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSizeSm);
    doc.setTextColor(0);
    doc.text("DYC/FMT-MR/45 Rev.00 ; Date-24/05/2023", config.borderMargin, pageNoY);
    doc.text(
      `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`,
      layout.pageWidth - config.margin - 80,
      pageNoY
    );
  }
};

// SECTION HEADING — gray bar, e.g. "Participant Information"
const addSectionHeading = (title) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   [[title]],
    theme:  "grid",
    styles: {
      ...getHeaderStyles(),
      cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
    },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(0);
};

// LABEL / VALUE TABLE — e.g. Employee Name | Rohit Barge
const addLabelValueTable = (rows, labelWidthRatio = 0.28) => {
  const labelW = layout.contentWidth * labelWidthRatio;
  const valueW = layout.contentWidth - labelW;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   rows.map(([label, value]) => [
      { content: label, styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: value || "", styles: getBaseStyles() },
    ]),
    theme:  "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: labelW }, 1: { cellWidth: valueW } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(0);
};

// FEEDBACK RATINGS TABLE
const addRatingsTable = (inputStates) => {
  addSectionHeading("Feedback Ratings (1 - 5) Tick the one");

  const questions = [
    { id: "overallSatisfactionRating",        label: "Overall Satisfaction with Training" },
    { id: "relevanceToRoleRating",            label: "Relevance to Your Role" },
    { id: "trainerEffectivenessRating",       label: "Trainer's Effectiveness" },
    { id: "qualityOfTrainingMaterialsRating", label: "Quality of Training Materials" },
  ];

  const ratingColW = (layout.contentWidth * 0.55) / 5;
  const srColW      = 25;
  const labelColW   = layout.contentWidth - srColW - ratingColW * 5;

  const head = [[
    { content: "Sr.No.", styles: getHeaderStyles() },
    { content: "Question", styles: getHeaderStyles() },
    ...[1, 2, 3, 4, 5].map((n) => ({ content: String(n), styles: { ...getHeaderStyles(), halign: "center" } })),
  ]];

  const body = questions.map((q, idx) => {
    const currentVal = Number(inputStates?.[q.id]) || 0;
    return [
      { content: idx + 1, styles: getBaseStyles() },
      { content: q.label, styles: getBaseStyles() },
      ...[1, 2, 3, 4, 5].map((n) => ({
        content: currentVal === n ? "\u2611" : "\u2610", // ☑ / ☐
        styles: { ...getBaseStyles(), halign: "center" },
      })),
    ];
  });

  // Row 5 — Training needs fulfilled Yes/No
  body.push([
    { content: 5, styles: getBaseStyles() },
    { content: "Training needs fully filled satisfactory", styles: getBaseStyles() },
    {
      content: `${inputStates?.trainingNeedsFulfilled === "Yes" ? "\u2611" : "\u2610"} Yes    ${inputStates?.trainingNeedsFulfilled === "No" ? "\u2611" : "\u2610"} No`,
      colSpan: 5,
      styles: { ...getBaseStyles(), halign: "center" },
    },
  ]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme:  "grid",
    styles: getBaseStyles(),
    columnStyles: {
      0: { cellWidth: srColW },
      1: { cellWidth: labelColW },
      2: { cellWidth: ratingColW }, 3: { cellWidth: ratingColW },
      4: { cellWidth: ratingColW }, 5: { cellWidth: ratingColW }, 6: { cellWidth: ratingColW },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// FREE-TEXT BLOCK — Comments / Suggestions
const addTextBlock = (title, text) => {
  addSectionHeading(title);
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body:   [[text || ""]],
    theme:  "grid",
    styles: { ...getBaseStyles(), minCellHeight: 30, valign: "top" },
    columnStyles: { 0: { cellWidth: layout.contentWidth } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// SIGNATURE / SUBMITTED BY BLOCK
const addSignatureBlock = (inputStates, employee) => {
  layout.ensureSpace(90);

  const halfW = layout.contentWidth / 2;
  const sigRowHeight = 60;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        { content: "Employee Signature:", styles: { ...getHeaderStyles(), halign: "left" } },
        { content: "", styles: { ...getHeaderStyles() } },
      ],
      [
        { content: "", styles: { minCellHeight: sigRowHeight, valign: "middle" } },
        {
          content: `Name: ${employee?.name || ""}\nDate: ${moment().format("DD/MM/YYYY")}`,
          styles: { minCellHeight: sigRowHeight, valign: "middle" },
        },
      ],
    ],
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: halfW }, 1: { cellWidth: halfW } },
    didDrawCell: (data) => {
      if (data.row.index === 1 && data.column.index === 0 && inputStates?.employeeSign) {
        const { x, y, width: w, height: h } = data.cell;
        const imgW = 90, imgH = 45;
        layout.doc.addImage(inputStates.employeeSign, "PNG", x + (w - imgW) / 2, y + (h - imgH) / 2, imgW, imgH);
      }
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// MAIN EXPORT
export async function generateTrainingFeedbackPDF(inputStates, employee, otherData, setDownloadPdfloading) {
  try {
    setDownloadPdfloading(true);

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos",      customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold,   "Aptos-Bold", customFontFamily.boldFont,   customFontFamily.bold),
    ]);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    layout.init(doc);

    addSectionHeading("Participant Information");
    addLabelValueTable([
      ["Employee Name", employee?.name],
      ["Department", otherData?.department || employee?.department],
      ["Position", otherData?.hrDesignation],
    ]);

    addSectionHeading("Training Details");
    addLabelValueTable([
      ["Employee Code", employee?.employeesCode],
      ["Training Title", inputStates?.trainingTitle],
      ["Training Provider", inputStates?.trainingProvider],
      [
        "Training Date",
        `${inputStates?.trainingStartDate ? moment(inputStates.trainingStartDate).format("DD/MM/YYYY") : ""} to ${inputStates?.trainingEndDate ? moment(inputStates.trainingEndDate).format("DD/MM/YYYY") : ""}`,
      ],
      ["Location", inputStates?.location],
    ]);

    addRatingsTable(inputStates);

    addTextBlock("Comments on Training received", inputStates?.commentsOnTraining);
    addTextBlock("Suggestions for improvement", inputStates?.suugestions);

    // addSignatureBlock(inputStates, employee);

    addPageNumbers(doc, doc.getNumberOfPages(), inputStates?.id ? `TF-${inputStates.id}` : "");

    doc.save(`Training_Feedback_${employee?.employeesCode || inputStates?.id || ""}.pdf`);
  } catch (error) {
    console.error("Training feedback PDF generation error:", error);
  } finally {
    setDownloadPdfloading(false);
  }
}